import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
} from "@mui/material";
import { FaPlus, FaEdit, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../../api";

import { Cropper, RectangleStencil } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";

export default function Schools() {
    const [loading, setLoading] = useState(true);
    const [schools, setSchools] = useState([]);
    const [subscriptionDays, setSubscriptionDays] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        id: "",
        name: "",
        code: "",
        address: "",
        phone: "",
        email: "",
        domain: "",
        subscription_amount: "",
        logo: null,
    });
    const [editId, setEditId] = useState(null);

    const [image, setImage] = useState(null);
    const [croppedImg, setCroppedImg] = useState(null);
    const [oldLogo, setOldLogo] = useState(null);
    const cropperRef = useRef(null);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const navigate = useNavigate();

    const fetchSchools = async () => {
        setLoading(true);
        try {
            const res = await api.get("/super_admin/school/list");
            const schoolList = res.data?.data || [];
            setSchools(schoolList);

            const subscriptionResults = await Promise.all(
                schoolList.map(async (school) => {
                    try {
                        const subscriptionRes = await api.get(
                            `/schoolsubscription/get_subscription/${school.id}`
                        );
                        return [school.id, subscriptionRes.data?.data?.due_date || null];
                    } catch (err) {
                        console.error(`Fetch subscription error for school ${school.id}:`, err);
                        return [school.id, null];
                    }
                })
            );

            const today = new Date();
            const todayAtMidnight = new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate()
            );

            const daysBySchool = Object.fromEntries(
                subscriptionResults.map(([id, dueDate]) => {
                    if (!dueDate) return [id, null];

                    const parts = dueDate.split("-").map(Number);
                    if (parts.length !== 3 || parts.some(Number.isNaN)) return [id, null];

                    const dueDateAtMidnight = new Date(parts[0], parts[1] - 1, parts[2]);
                    const days = Math.ceil(
                        (dueDateAtMidnight.getTime() - todayAtMidnight.getTime()) /
                        (1000 * 60 * 60 * 24)
                    );

                    return [id, days];
                })
            );

            setSubscriptionDays(daysBySchool);
        } catch (err) {
            console.error("Fetch schools error:", err);
            setSubscriptionDays({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchools();
    }, []);

    const handleOpen = (school = null) => {
        if (school) {
            setFormData({
                id: school.id,
                name: school.name,
                code: school.code,
                address: school.address || "",
                phone: school.phone || "",
                email: school.email || "",
                domain: school.domain || "",
                subscription_amount: school.subscription_amount || "",
                logo: null,
            });
            setEditId(school.id);
            setCroppedImg(null);
            setOldLogo(
                school.logo
                    ? `https://dgps.co.in/public/admin/assets/img/schools/logo/${school.logo}`
                    : null
            );
        } else {
            setFormData({
                id: "",
                name: "",
                code: "",
                address: "",
                phone: "",
                email: "",
                domain: "",
                subscription_amount: "",
                logo: null,
            });
            setEditId(null);
            setCroppedImg(null);
            setOldLogo(null);
        }
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setFormData({
            id: "",
            name: "",
            code: "",
            address: "",
            phone: "",
            email: "",
            domain: "",
            subscription_amount: "",
            logo: null,
        });
        setEditId(null);
        setCroppedImg(null);
        setOldLogo(null);
    };

    const handleSave = async () => {
        try {
            const fd = new FormData();
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null && formData[key] !== "") {
                    fd.append(key, formData[key]);
                }
            });

            let res;
            if (editId) {
                res = await api.post("/super_admin/school/update", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                res = await api.post("/super_admin/school/add", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            setSnackbar({ open: true, message: res.data?.message || "Success", severity: "success" });

            handleClose();
            fetchSchools();
        } catch (err) {
            console.error("Save error:", err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || "Error occurred",
                severity: "error",
            });
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type !== "image/png") {
                alert("Only PNG images are allowed!");
                e.target.value = "";
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
                setCroppedImg(null);
                setOldLogo(null);
            };
            reader.readAsDataURL(file);
        }
    };

    const doCrop = () => {
        if (cropperRef.current) {
            const canvas = cropperRef.current.getCanvas();
            if (canvas) {
                canvas.toBlob((blob) => {
                    if (blob) {
                        const file = new File([blob], "logo.png", { type: "image/png" });
                        setFormData((prev) => ({ ...prev, logo: file }));
                        setCroppedImg(URL.createObjectURL(blob));
                        setImage(null);
                        setOldLogo(null);
                    }
                }, "image/png");
            }
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 2, background: "rgba(255,255,255,0.58)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2.5, alignItems: "center", gap: 2 }}>
                <Box>
                <Typography variant="h5" fontWeight={900} sx={{ color: "#102a43" }}>
                    Schools Management
                </Typography>
                <Typography variant="body2" sx={{ color: "#607086", mt: 0.5, fontWeight: 600 }}>
                    Manage schools and monitor subscription status
                </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<FaPlus />}
                    onClick={() => handleOpen()}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800, flexShrink: 0 }}
                >
                    Add School
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto", boxShadow: "0 14px 34px rgba(16,42,67,0.1)", border: "1px solid rgba(78,102,135,0.14)" }}>
                    <Table sx={{ minWidth: 850 }}>
                        <TableHead sx={{ bgcolor: "#123a5a" }}>
                            <TableRow>
                                {['ID', 'Logo', 'Name', 'Code', 'Phone', 'Subscription Days', 'Actions'].map((heading) => (
                                    <TableCell key={heading} align={heading === "Actions" ? "right" : "left"} sx={{ color: "#fff", fontWeight: 800, whiteSpace: "nowrap", borderBottom: 0 }}>
                                        {heading}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schools.map((school) => (
                                <TableRow key={school.id} hover sx={{ "&:nth-of-type(even)": { backgroundColor: "#f7fafc" }, "& td": { borderColor: "rgba(78,102,135,0.12)" } }}>
                                    <TableCell sx={{ fontWeight: 800, color: "#102a43" }}>{school.id}</TableCell>
                                    <TableCell>
                                        {school.logo ? (
                                            <img
                                                src={`https://dgps.co.in/public/admin/assets/img/schools/logo/${school.logo}`}
                                                alt="Logo"
                                                style={{ width: "42px", height: "42px", borderRadius: "8px", objectFit: "contain", border: "1px solid #e2e8f0" }}
                                            />
                                        ) : (
                                            "-"
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: "#172033" }}>{school.name}</TableCell>
                                    <TableCell>{school.code}</TableCell>
                                    <TableCell>{school.phone}</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: subscriptionDays[school.id] < 0 ? "#be123c" : "#0f766e", whiteSpace: "nowrap" }}>
                                        {subscriptionDays[school.id] === null || subscriptionDays[school.id] === undefined
                                            ? "-"
                                            : `${subscriptionDays[school.id]} Days`}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<FaEye />}
                                            onClick={() => navigate(`/super_admin/schools/${school.id}`)}
                                            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800, color: "#fff", background: "linear-gradient(135deg, #0f766e, #0891b2)", boxShadow: "none", mr: 0.75, "&:hover": { boxShadow: "0 6px 14px rgba(15,118,110,0.24)" } }}
                                        >
                                            View Profile
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<FaEdit />}
                                            onClick={() => handleOpen(school)}
                                            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800, color: "#2563eb", borderColor: "rgba(37,99,235,0.45)" }}
                                        >
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!schools.length && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No schools found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editId ? "Edit School" : "Add School"}</DialogTitle>
                <DialogContent>
                    <TextField margin="dense" label="School Name" fullWidth value={formData.name}
                        onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))} />
                    <TextField margin="dense" label="School Code" fullWidth value={formData.code}
                        onChange={(e) => setFormData((f) => ({ ...f, code: e.target.value }))} />
                    <TextField margin="dense" label="Address" fullWidth value={formData.address}
                        onChange={(e) => setFormData((f) => ({ ...f, address: e.target.value }))} />
                    <TextField margin="dense" label="Phone" fullWidth value={formData.phone}
                        onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))} />
                    <TextField margin="dense" label="Email" fullWidth value={formData.email}
                        onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))} />
                    <TextField margin="dense" label="Domain" fullWidth value={formData.domain}
                        onChange={(e) => setFormData((f) => ({ ...f, domain: e.target.value }))} />
                    <TextField margin="dense" label="Subscription Amount" type="number" fullWidth
                        value={formData.subscription_amount}
                        onChange={(e) => setFormData((f) => ({ ...f, subscription_amount: e.target.value }))} />

                    {/* Logo */}
                    <Box mt={2}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Logo</Typography>
                        {oldLogo && !image && !croppedImg && (
                            <Box>
                                <img src={oldLogo} alt="Old Logo" style={{ width: "100px", borderRadius: "6px" }} />
                                <Box mt={1}>
                                    <Button size="small" onClick={() => setOldLogo(null)}>Change Logo</Button>
                                </Box>
                            </Box>
                        )}
                        {!image && !croppedImg && !oldLogo && (
                            <input type="file" accept="image/png" onChange={handleFileChange} />
                        )}
                        {image && !croppedImg && (
                            <Box sx={{ mt: 2 }}>
                                <Cropper ref={cropperRef} src={image} stencilComponent={RectangleStencil}
                                    className="cropper" style={{ height: 200, width: "100%", borderRadius: "6px" }} />
                                <Box mt={2} sx={{ display: "flex", gap: 2 }}>
                                    <Button onClick={() => setImage(null)}>Cancel</Button>
                                    <Button variant="contained" onClick={doCrop}>Crop & Save</Button>
                                </Box>
                            </Box>
                        )}
                        {croppedImg && (
                            <Box mt={2}>
                                <img src={croppedImg} alt="Cropped Logo" style={{ width: "100px", borderRadius: "6px" }} />
                                <Box mt={1}>
                                    <Button size="small" onClick={() => { setImage(null); setCroppedImg(null); setFormData((f) => ({ ...f, logo: null })); }}>
                                        Change Logo
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">
                        {editId ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }} // ✅ Center Top
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
}
